import { getApiErrorMessage, IApiAxiosError } from "@/api/api.types";
import {
    deleteDocumentCustomFieldProperty,
    deleteDocumentExtractedProperty,
    updateDocumentCustomFieldProperty,
    updateDocumentExtractedProperty,
} from "@/api/documents";
import { ConfirmationDialog } from "@/components/shared/confirmation-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import { cn } from "@/lib/utils";
import {
    clampPageInput,
    isEmptyPropertyValue,
    pageNumberToString,
} from "@/utils/fields";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Pencil, PencilLine, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { SourceQuotePopover } from "./SourceQuotePopover";

export interface ExtractedFieldRowProps {
    documentId: number;
    propertyId: number;
    propertyName: string;
    value: string;
    isEdited: boolean;
    pageNumber: number | null;
    /** Total pages in the document, used to bound the page_number input */
    totalPages?: number;
    /** Whether this is a user-created custom property (suppresses the edited dot) */
    isCustom?: boolean;
    /**
     * Slice 7 (wire-shape). The verbatim quote backing the extracted value.
     * Null/empty falls back to "Source on page X" copy in the popover. The
     * page badge is the popover trigger; rendering of any source UI is
     * gated on `pageNumber !== null` (regardless of source).
     */
    source?: string | null;
    /**
     * Slice 7. Included in the `pdfModal` payload when the user clicks
     * "Open in document". Different from `propertyName` for nested array
     * fields where the row's name is the property and the field name is
     * the parent (e.g., "Locations").
     */
    fieldName?: string;
    /** Slice 7. Bubbles up to DocumentDetailPanel's pdfModal state. */
    onOpenInDocument?: (payload: {
        documentId: number;
        propertyId: number;
        pageNumber: number;
        fieldName: string;
        source: string | null;
    }) => void;
    /**
     * Slice 11 (adapter override). When provided, replaces the default
     * `updateDocumentExtractedProperty` (or `…CustomFieldProperty`) save
     * mutation. The host owns cache invalidation and error toasts. Used by
     * `ExtractedCOISection` to route edits through `updateDocumentCoi`.
     */
    onSave?: (data: {
        value: string | null;
        pageNumber: number | null;
    }) => Promise<void> | void;
    /**
     * Slice 11. Hides the delete affordance. COI standard fields cannot be
     * "deleted" per-field (they're keys on a single COI record), so the
     * section passes `false`. Defaults to `true`.
     */
    showDelete?: boolean;
    /**
     * Slice 11. Locks the row into a non-editable display. Used for
     * coverage rows inside `ExtractedCOISection` where the coverages array
     * is read-only. The value renders as static text and all action
     * buttons are suppressed.
     */
    readOnly?: boolean;
    /**
     * Slice 11. Suppresses the page-number input in edit mode. The
     * read-mode page badge still renders for visual consistency, but the
     * user cannot edit the page in the inline form. Used by
     * `ExtractedCOISection` because the COI has a single doc-level
     * `page_num` (not a per-property page) and the host's `onSave`
     * intentionally drops `pageNumber`. Without this, the user could type
     * a new page number and have it silently discarded on save.
     */
    hidePageInput?: boolean;
}

type RowMode = "read" | "edit";

export function ExtractedFieldRow({
    documentId,
    propertyId,
    propertyName,
    value,
    isEdited,
    pageNumber,
    totalPages,
    isCustom = false,
    source = null,
    fieldName,
    onOpenInDocument,
    onSave,
    showDelete = true,
    readOnly = false,
    hidePageInput = false,
}: ExtractedFieldRowProps) {
    const queryClient = useQueryClient();

    const [mode, setMode] = useState<RowMode>("read");
    const [draftValue, setDraftValue] = useState(value ?? "");
    const [draftPage, setDraftPage] = useState(pageNumberToString(pageNumber));
    const [confirmDelete, setConfirmDelete] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const editRowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mode === "read") {
            setDraftValue(value ?? "");
            setDraftPage(pageNumberToString(pageNumber));
        }
    }, [value, pageNumber, mode]);

    useEffect(() => {
        if (mode === "edit") {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [mode]);

    const isEmpty = isEmptyPropertyValue(value);

    const updateMutation = useMutation({
        mutationFn: async ({
            newValue,
            newPage,
        }: {
            newValue: string | null;
            newPage: number | null;
        }) => {
            // Slice 11. When an `onSave` adapter is supplied, route the save
            // through it and skip the property-backed mutation calls. The host
            // owns the network request, cache invalidation, and error UX.
            if (onSave) {
                await onSave({ value: newValue, pageNumber: newPage });
                return;
            }
            const body = { value: newValue, page_number: newPage };
            if (isCustom) {
                await updateDocumentCustomFieldProperty(
                    documentId,
                    propertyId,
                    body
                );
            } else {
                await updateDocumentExtractedProperty(
                    documentId,
                    propertyId,
                    body
                );
            }
        },
        onSuccess: () => {
            // The host's `onSave` already drives its own invalidation; firing
            // the extracted-fields query key here would be wrong (e.g., COI
            // edits live under a different key) AND wasteful.
            if (onSave) return;
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
        },
        onError: (error: IApiAxiosError) => {
            // Defer to the host's error toast when adapting; otherwise we'd
            // double-toast on a single failure.
            if (onSave) return;
            toast.error(getApiErrorMessage(error, "Failed to save property"));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () =>
            isCustom
                ? deleteDocumentCustomFieldProperty(documentId, propertyId)
                : deleteDocumentExtractedProperty(documentId, propertyId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
        },
        onError: (error: IApiAxiosError) => {
            toast.error(getApiErrorMessage(error, "Failed to delete property"));
        },
    });

    const commitSave = useCallback(() => {
        const trimmed = draftValue.trim();
        const normalized = trimmed === "" ? null : trimmed;
        const pageNum = draftPage.trim() ? parseInt(draftPage, 10) : null;
        const inRange =
            pageNum !== null &&
            !isNaN(pageNum) &&
            pageNum >= 1 &&
            (!totalPages || pageNum <= totalPages);
        const normalizedPage = inRange ? pageNum : null;

        const originalValue = value ?? null;
        const originalPage = pageNumber ?? null;

        if (normalized !== originalValue || normalizedPage !== originalPage) {
            updateMutation.mutate({
                newValue: normalized,
                newPage: normalizedPage,
            });
        }
        setMode("read");
    }, [draftValue, draftPage, totalPages, value, pageNumber, updateMutation]);

    const cancelEdit = useCallback(() => {
        setDraftValue(value ?? "");
        setDraftPage(pageNumberToString(pageNumber));
        setMode("read");
    }, [value, pageNumber]);

    const handleEdit = useCallback(() => {
        setDraftValue(value ?? "");
        setDraftPage(pageNumberToString(pageNumber));
        setMode("edit");
    }, [value, pageNumber]);

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                commitSave();
            }
            // Escape is handled by the native row-level listener below so it
            // can stopPropagation BEFORE the panel's native Esc listener fires.
            // React synthetic events dispatch only after the native bubble has
            // already visited every ancestor, which is too late to spare the panel.
        },
        [commitSave]
    );

    // Native (not synthetic) keydown listener on the row's edit-mode container.
    // The panel's `usePanelEscape` attaches a native listener on `panelRef`;
    // because native bubble visits ancestors in DOM order before reaching the
    // React root, a synthetic-only Esc handler can't preempt it. Stopping
    // propagation here keeps the panel from closing while the user is editing.
    // Slice 6's planned state-lift will make this redundant; until then this
    // guard is what keeps inline-edit Esc from dismissing the whole panel.
    useEffect(() => {
        if (mode !== "edit") return;
        const node = editRowRef.current;
        if (!node) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            cancelEdit();
        };
        node.addEventListener("keydown", handler);
        return () => node.removeEventListener("keydown", handler);
    }, [mode, cancelEdit]);

    const handleInputBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const row = e.currentTarget.closest('[role="row"]');
            if (row && e.relatedTarget && row.contains(e.relatedTarget)) return;
            if (mode === "edit") commitSave();
        },
        [mode, commitSave]
    );

    const handleCopy = useCallback(() => {
        if (!value) return;
        // Prefix the value with field context so paste targets (chat, email,
        // a spreadsheet cell) carry meaning. For nested array rows the field
        // name and property name are distinct (`Locations` / `Address`); for
        // scalar fields they're typically identical, so dedupe to avoid
        // `Per Occurrence Limit Per Occurrence Limit - 500000`.
        const label =
            fieldName && fieldName !== propertyName
                ? `${fieldName} ${propertyName}`
                : propertyName;
        void copyToClipboardWithToast(`${label} - ${value}`);
    }, [value, fieldName, propertyName]);

    const customMarker = isCustom ? (
        <PencilLine
            size={10}
            className="text-muted-foreground/40 shrink-0"
            aria-label="User-added property"
        />
    ) : null;

    const showEditedDot = !isCustom && isEdited && !isEmpty;

    if (mode === "edit") {
        return (
            <div
                ref={editRowRef}
                data-testid="extracted-field-row"
                role="row"
                aria-label={`Editing ${propertyName}`}
                className="grid grid-cols-[minmax(120px,150px)_1fr_auto] items-start gap-3 px-3 py-2.5 @max-md:grid-cols-[1fr_auto] @max-md:gap-x-2 @max-md:gap-y-1.5"
            >
                <div className="flex min-w-0 items-center gap-1.5 pt-1.5 @max-md:col-span-2 @max-md:pt-0">
                    <span
                        title={propertyName}
                        className="text-foreground truncate text-sm font-medium"
                    >
                        {propertyName}
                    </span>
                    {customMarker}
                </div>

                <div className="min-w-0 @max-md:col-start-1 @max-md:row-start-2">
                    <Input
                        ref={inputRef}
                        value={draftValue}
                        onChange={(e) => setDraftValue(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleInputBlur}
                        className="h-7 w-full text-sm"
                        aria-label={`Value for ${propertyName}`}
                        placeholder="Enter value…"
                    />
                    {pageNumber !== null && !hidePageInput && (
                        <div className="mt-2 flex items-center gap-1.5">
                            <span className="text-muted-foreground font-mono text-[10px]">
                                p.{pageNumber}
                            </span>
                            <Input
                                type="number"
                                placeholder="Pg"
                                min={1}
                                max={totalPages}
                                value={draftPage}
                                onChange={(e) => {
                                    const clamped = clampPageInput(
                                        e.target.value,
                                        totalPages
                                    );
                                    if (clamped !== null) setDraftPage(clamped);
                                }}
                                onKeyDown={handleInputKeyDown}
                                onBlur={handleInputBlur}
                                className="h-7 w-14 [appearance:textfield] text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                aria-label={`Page number for ${propertyName}`}
                            />
                        </div>
                    )}
                </div>

                <div className="flex items-start pt-1 @max-md:col-start-2 @max-md:row-start-2 @max-md:pt-0.5">
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={commitSave}
                        disabled={updateMutation.isPending}
                        aria-label="Save"
                    >
                        <Check />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                data-testid="extracted-field-row"
                role="row"
                aria-label={propertyName}
                className="group/row grid grid-cols-[minmax(120px,150px)_1fr_auto] items-start gap-3 px-3 py-2.5 @max-md:grid-cols-[1fr_auto] @max-md:gap-x-2 @max-md:gap-y-1"
            >
                <div className="flex min-w-0 items-center gap-1.5 pt-0.5 @max-md:col-span-2 @max-md:pt-0">
                    <span
                        title={propertyName}
                        className="text-foreground truncate text-sm font-medium"
                    >
                        {propertyName}
                    </span>
                    {customMarker}
                    {showEditedDot && (
                        <span
                            data-testid="edited-dot"
                            title="Edited"
                            className="inline-block size-1.5 shrink-0 rounded-full bg-amber-500"
                        >
                            <span className="sr-only">edited</span>
                        </span>
                    )}
                </div>

                <div className="min-w-0 @max-md:col-start-1 @max-md:row-start-2">
                    {readOnly ? (
                        <span
                            className={cn(
                                "block text-sm leading-snug break-words",
                                isEmpty
                                    ? "text-muted-foreground/50"
                                    : "text-foreground"
                            )}
                        >
                            {isEmpty ? "—" : value}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={handleEdit}
                            aria-label={
                                isEmpty
                                    ? `${propertyName}: no value`
                                    : undefined
                            }
                            className={cn(
                                "block w-full rounded text-left text-sm leading-snug break-words",
                                "text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                                "focus-visible:ring-ring/40 outline-none focus-visible:ring-2"
                            )}
                        >
                            {isEmpty ? (
                                <span className="text-muted-foreground/50">
                                    —
                                </span>
                            ) : (
                                value
                            )}
                        </button>
                    )}
                    {pageNumber !== null &&
                        (onOpenInDocument ? (
                            <SourceQuotePopover
                                documentId={documentId}
                                propertyId={propertyId}
                                fieldName={
                                    fieldName
                                        ? `${fieldName} - ${propertyName}`
                                        : propertyName
                                }
                                pageNumber={pageNumber}
                                source={source ?? null}
                                onOpenInDocument={onOpenInDocument}
                            >
                                <button
                                    type="button"
                                    className={cn(
                                        "border-border bg-muted text-muted-foreground mt-2 inline-flex h-5 shrink-0 items-center rounded-full border px-1.5",
                                        "font-mono text-[10px] leading-none font-medium",
                                        "hover:text-copper focus-visible:ring-ring/40 outline-none focus-visible:ring-2"
                                    )}
                                >
                                    p.{pageNumber}
                                </button>
                            </SourceQuotePopover>
                        ) : (
                            <button
                                type="button"
                                className={cn(
                                    "border-border bg-muted text-muted-foreground mt-2 inline-flex h-5 shrink-0 items-center rounded-full border px-1.5",
                                    "font-mono text-[10px] leading-none font-medium",
                                    "hover:text-copper focus-visible:ring-ring/40 outline-none focus-visible:ring-2"
                                )}
                            >
                                p.{pageNumber}
                            </button>
                        ))}
                </div>

                {!readOnly && (
                    <div className="flex items-start gap-0.5 pt-0.5 @max-md:col-start-2 @max-md:row-start-2 @max-md:pt-0">
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={handleCopy}
                            aria-label="Copy value"
                            className="opacity-0 transition-opacity duration-150 group-focus-within/row:opacity-100 group-hover/row:opacity-100"
                        >
                            <Copy />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={handleEdit}
                            aria-label="Edit"
                        >
                            <Pencil />
                        </Button>
                        {showDelete && (
                            <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => setConfirmDelete(true)}
                                disabled={deleteMutation.isPending}
                                aria-label={`Delete ${propertyName}`}
                            >
                                <Trash2 />
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {!readOnly && showDelete && (
                <ConfirmationDialog
                    open={confirmDelete}
                    onOpenChange={setConfirmDelete}
                    title={`Delete ${propertyName}?`}
                    description="This cannot be undone."
                    variant="destructive"
                    onConfirm={() => deleteMutation.mutate()}
                />
            )}
        </>
    );
}
